import { Link } from "@tanstack/react-router";
import { RELEASES } from "@/lib/content";

/**
 * Compact, semantic internal-link cluster for a release's song authority
 * pages. Rendered outside the album page's visual tracklist so crawlers reach
 * every song page from release context (e.g. the official announcement) with
 * plain <a href> links and no keyword padding.
 */
export function ReleaseTrackCluster({
  releaseSlug,
  className = "",
}: {
  releaseSlug: string;
  className?: string;
}) {
  const release = RELEASES.find((r) => r.slug === releaseSlug);
  const tracks = (release?.tracks ?? []).filter((t) => Boolean(t.slug));
  if (!release || tracks.length === 0) return null;

  return (
    <nav aria-labelledby={`explore-${release.slug}`} className={`mt-12 ${className}`}>
      <h2 id={`explore-${release.slug}`} className="font-serif text-2xl">
        Explore {release.title}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Every track has its own page with the writing behind it.
      </p>
      <ul className="mt-4 flex flex-wrap gap-2 text-sm">
        {tracks.map((t) => (
          <li key={t.slug}>
            <Link
              to="/music/$slug/song/$song"
              params={{ slug: release.slug, song: t.slug! }}
              className="inline-flex rounded-full border hairline px-3 py-1.5 hover:border-gold hover:text-gold"
            >
              {t.title}
            </Link>
          </li>
        ))}
      </ul>
      <Link
        to="/music/$slug"
        params={{ slug: release.slug }}
        className="mt-4 inline-block text-sm text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous"
      >
        {release.title} — full release page
      </Link>
    </nav>
  );
}
